import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { Kiosk } from "../../types/kiosk.type";
import { KioskConfiguration } from "../../types/kioskConfig.types";
import { copyKioskConfigToTargets, CopyKioskConfigResult } from "../../services/KioskConfigService";
import { Button } from "../Button";
import "./CopyKioskConfigModal.css";

interface CopyKioskConfigModalProps {
  open: boolean;
  sourceKiosk: Kiosk;
  sourceConfig: KioskConfiguration | null;
  allKiosks: Kiosk[];
  onClose: () => void;
}

type CopyPhase = "select" | "copying" | "results";

const copySections = [
  { id: "attract", label: "Attract Screen" },
  { id: "donate", label: "Donate Screen" },
  { id: "thanks", label: "Thanks Screen" },
  { id: "tiers", label: "Donation Tiers" },
  { id: "branding", label: "Partner Branding" },
  { id: "currency", label: "Currency" },
] as const;

type CopySection = (typeof copySections)[number]["id"];
const allCopySections = new Set<CopySection>(copySections.map((section) => section.id));

const CopyKioskConfigModal: React.FC<CopyKioskConfigModalProps> = ({
  open,
  sourceKiosk,
  sourceConfig,
  allKiosks,
  onClose,
}) => {
  const [phase, setPhase] = useState<CopyPhase>("select");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectedSections, setSelectedSections] = useState<Set<CopySection>>(
    () => new Set(allCopySections),
  );
  const [results, setResults] = useState<CopyKioskConfigResult[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [sourceError, setSourceError] = useState<string | null>(null);

  const eligibleKiosks = allKiosks.filter((k) => String(k.id) !== String(sourceKiosk.id));
  const filteredKiosks = eligibleKiosks.filter((k) => {
    const q = searchQuery.toLowerCase();
    return (
      !q ||
      (k.name || k.kioskId).toLowerCase().includes(q) ||
      (k.location || "").toLowerCase().includes(q) ||
      k.kioskId.toLowerCase().includes(q)
    );
  });

  useEffect(() => {
    if (!open) {
      setPhase("select");
      setSelectedIds(new Set());
      setSelectedSections(new Set(allCopySections));
      setResults([]);
      setSearchQuery("");
      setSourceError(null);
    }
  }, [open]);

  const handleToggle = (kioskId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(kioskId)) {
        next.delete(kioskId);
      } else {
        next.add(kioskId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === filteredKiosks.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredKiosks.map((k) => String(k.id))));
    }
  };

  const handleSectionToggle = (section: CopySection) => {
    setSelectedSections((previous) => {
      const next = new Set(previous);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  const handleCopy = async () => {
    if (selectedIds.size === 0 || selectedSections.size === 0) return;
    setSourceError(null);
    setPhase("copying");

    const targets = allKiosks
      .filter((k) => selectedIds.has(String(k.id)))
      .map((k) => ({ id: k.kioskId, name: k.name || k.kioskId }));

    try {
      const copyResults = await copyKioskConfigToTargets(
        sourceKiosk.kioskId,
        targets,
        Array.from(selectedSections),
      );
      setResults(copyResults);
      setPhase("results");
    } catch (err) {
      const msg = (err as Error)?.message || "Failed to copy configuration";
      const isNotFound = msg.toLowerCase().includes("not found");
      setSourceError(
        isNotFound
          ? `"${sourceKiosk.name || sourceKiosk.kioskId}" has no saved configuration yet. Configure it first, then copy.`
          : msg,
      );
      setPhase("select");
    }
  };

  const successCount = results.filter((r) => r.success).length;
  const failCount = results.filter((r) => !r.success).length;

  if (!open) return null;

  const modal = (
    <div className="copy-config-overlay" role="dialog" aria-modal="true" aria-label="Copy Kiosk Configuration">
      <div className="copy-config-modal">
        {/* Header */}
        <div className="copy-config-header">
          <div>
            <h2 className="copy-config-title">Copy Kiosk Configuration</h2>
            <p className="copy-config-subtitle">
              Copying from: <strong>{sourceKiosk.name || sourceKiosk.kioskId}</strong>
            </p>
          </div>
          <button className="copy-config-close-btn" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        {/* Configuration-section selection */}
        {sourceConfig && phase === "select" && (
          <div className="copy-config-source-preview">
            <span className="source-preview-label">Select what to copy</span>
            <div className="copy-config-section-list">
              {copySections.map((section) => {
                const checked = selectedSections.has(section.id);
                const tierLabel =
                  section.id === "tiers" && sourceConfig.donationAmounts?.length
                    ? ` (${sourceConfig.donationAmounts.length} tiers)`
                    : "";

                return (
                  <label key={section.id} className={`copy-config-section-item ${checked ? "selected" : ""}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handleSectionToggle(section.id)}
                    />
                    <span>{section.label}{tierLabel}</span>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {/* Body */}
        <div className="copy-config-body">
          {sourceError && (
            <div className="copy-config-source-error">
              <span className="source-error-icon">⚠️</span>
              <span>{sourceError}</span>
            </div>
          )}
          {phase === "select" && (
            <>
              {eligibleKiosks.length === 0 ? (
                <div className="copy-config-empty">No other kiosks available to copy to.</div>
              ) : (
                <>
                  <div className="copy-config-controls">
                    <input
                      className="copy-config-search"
                      type="text"
                      placeholder="Search kiosks by name, location, or ID..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <button
                      className="select-all-btn"
                      onClick={handleSelectAll}
                      type="button"
                    >
                      {selectedIds.size === filteredKiosks.length && filteredKiosks.length > 0
                        ? "Deselect All"
                        : "Select All"}
                    </button>
                  </div>

                  {filteredKiosks.length === 0 ? (
                    <div className="copy-config-empty">No kiosks match your search.</div>
                  ) : (
                    <ul className="copy-config-kiosk-list">
                      {filteredKiosks.map((kiosk) => {
                        const id = String(kiosk.id);
                        const checked = selectedIds.has(id);
                        return (
                          <li
                            key={id}
                            className={`copy-config-kiosk-item ${checked ? "selected" : ""}`}
                            onClick={() => handleToggle(id)}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => handleToggle(id)}
                              onClick={(e) => e.stopPropagation()}
                              id={`kiosk-check-${id}`}
                              aria-label={`Select ${kiosk.name || kiosk.kioskId}`}
                            />
                            <div className="kiosk-item-info">
                              <span className="kiosk-item-name">{kiosk.name || kiosk.kioskId}</span>
                              <span className="kiosk-item-meta">
                                {kiosk.kioskId}
                                {kiosk.location ? ` · ${kiosk.location}` : ""}
                              </span>
                            </div>
                            <span className={`kiosk-item-status ${kiosk.active ? "active" : "inactive"}`}>
                              {kiosk.active ? "Active" : "Inactive"}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </>
              )}
            </>
          )}

          {phase === "copying" && (
            <div className="copy-config-progress">
              <div className="copy-config-spinner" />
              <p>Copying configuration to {selectedIds.size} kiosk{selectedIds.size !== 1 ? "s" : ""}...</p>
            </div>
          )}

          {phase === "results" && (
            <div className="copy-config-results">
              <div className={`results-summary ${failCount > 0 ? "has-errors" : "all-success"}`}>
                {successCount > 0 && (
                  <span className="results-success-count">✓ {successCount} kiosk{successCount !== 1 ? "s" : ""} updated</span>
                )}
                {failCount > 0 && (
                  <span className="results-fail-count">✗ {failCount} failed</span>
                )}
              </div>
              <ul className="results-list">
                {results.map((r) => (
                  <li key={r.kioskId} className={`result-item ${r.success ? "success" : "failure"}`}>
                    <span className="result-icon">{r.success ? "✓" : "✗"}</span>
                    <span className="result-name">{r.kioskName}</span>
                    {r.error && <span className="result-error">{r.error}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="copy-config-footer">
          {phase === "select" && (
            <>
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleCopy}
                disabled={selectedIds.size === 0 || selectedSections.size === 0}
              >
                Copy to {selectedIds.size > 0 ? `${selectedIds.size} Kiosk${selectedIds.size !== 1 ? "s" : ""}` : "Selected Kiosks"}
              </Button>
            </>
          )}
          {phase === "copying" && (
            <Button variant="secondary" disabled>
              Please wait...
            </Button>
          )}
          {phase === "results" && (
            <Button variant="primary" onClick={onClose}>
              Done
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default CopyKioskConfigModal;
